import type {CarItem, ListingFilters} from "@/types";
import {createAsyncThunk, createSlice, type PayloadAction} from "@reduxjs/toolkit";
import type {RootState} from "@/store";
import {createVehicleThunk} from "@/store/thunks/createVehicleThunk.ts";
import {supabase} from "@/utils/supabase.ts";
import {keysToCamelCase} from "@/utils";
import {omit} from "lodash";



interface ListingState {
    listings: CarItem[];
    currentListing?: CarItem;
    loading: boolean;
    hasError: boolean;
    errorMessage?: string;
    page: number;
    totalPages: number;
    filters: ListingFilters;
    fetchedPages: number[];
}

const PAGE_SIZE = 20;

const initialState: ListingState = {
    fetchedPages: [],
    filters: {},
    hasError: false,
    listings: [],
    loading: false,
    page: 0,
    totalPages: 0
};

function applyFilters(listings: CarItem[], filters: ListingFilters): CarItem[] {
    return listings.filter((item) => {
        if (filters.brand && item.brand.toLowerCase() !== filters.brand.toLowerCase()) return false;
        if (filters.model && item.model.toLowerCase() !== filters.model.toLowerCase()) return false;
        if (filters.body && item.body?.toLowerCase() !== filters.body.toLowerCase()) return false;
        if (filters.transmission && item.transmission !== filters.transmission) return false;
        if (typeof filters.minPrice === "number" && item.price < filters.minPrice) return false;
        if (typeof filters.maxPrice === "number" && item.price > filters.maxPrice) return false;

        if (filters.query) {
            const q = filters.query.toLowerCase();
            const haystack = `${item.year} ${item.brand} ${item.model} ${item.name}`.toLowerCase();
            if (!haystack.includes(q)) return false;
        }

        return true;
    });
}

export function fallbackDealer() {
    return {
        id: "",
        name: "Dealer",
        profile: "",
        listingCount: 0,
        soldCount: 0,
        views: 0
    };
}

function mapDbListingToCarItem(row: any, currentUser: RootState["authentication"]["user"]): CarItem {
    const vehicle = row?.vehicle ?? {};
    const seller = currentUser ?? fallbackDealer();
    console.log(row);
    const cleaned = omit(row, ["vehicle", 'id', 'vehicle_id']);

    return {...keysToCamelCase(cleaned), ...keysToCamelCase(vehicle), seller} as CarItem

}

export const fetchListingsAsync = createAsyncThunk<
    { listings: CarItem[]; page: number; totalPages: number },
    number | undefined,
    { state: RootState; rejectValue: string }
>("listing/fetch", async (page, {getState, rejectWithValue}) => {
    const state = getState();
    const {fetchedPages, filters} = state.listing;
    const currentUser = state.authentication.user;

    try {
        if (!currentUser) {
            return rejectWithValue("No authenticated dealer");
        }

        const currentPage = page ?? 1;

        if (currentPage < 1) {
            return rejectWithValue("Invalid page number");
        }

        if (fetchedPages.includes(currentPage)) {
            return {
                listings: [],
                page: currentPage,
                totalPages: state.listing.totalPages || 1
            };
        }

        const start = (currentPage - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE - 1;

        const dbResponse = await supabase
            .from("listings")
            .select(
                "id,vehicle_id,price,negotiable,status,created_at,vehicle:vehicles!inner(*)",
                {count: "exact"}
            )
            .eq("vehicle.seller_id", currentUser.id)
            .order("created_at", {ascending: false})
            .range(start, end);

        if (dbResponse.error) {
            return rejectWithValue(dbResponse.error.message);
        }

        const totalRows = dbResponse.count ?? 0;
        const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

        if (currentPage > totalPages) {
            return rejectWithValue("Requested page is out of range");
        }

        const mapped = (dbResponse.data ?? []).map((row) => mapDbListingToCarItem(row, currentUser));
        const listings = applyFilters(mapped, filters);

        return {
            listings,
            page: currentPage,
            totalPages
        };
    } catch (error) {
        if (error instanceof Error) {
            return rejectWithValue(error.message);
        }
        return rejectWithValue("An error occurred");
    }
});

export const setCurrentListingAsync = createAsyncThunk<
    CarItem,
    string | number,
    { state: RootState; rejectValue: string }
>("listing/setCurrent", async (id, {getState, rejectWithValue}) => {
    try {
        const listingId = String(id);
        if (!listingId) {
            return rejectWithValue("Invalid listing id");
        }

        const state = getState();
        const currentUser = state.authentication.user;
        if (!currentUser) {
            return rejectWithValue("No authenticated dealer");
        }
        const listing = state.listing.listings.find((item) => item.id === listingId);
        if (listing) return listing;

        const [dbResponse, leadCountResponse] = await Promise.all([
            supabase
                .from("listings")
                .select("id,vehicle_id,price,negotiable,status,created_at,vehicle:vehicles!inner(*)")
                .eq("vehicle_id", listingId)
                .eq("vehicle.seller_id", currentUser.id)
                .single(),
            supabase
                .from("vehicle_events")
                .select("*", {count: "exact", head: true})
                .eq("vehicle_id", listingId)
                .eq("dealer_id", currentUser.id)
                .eq("event_type", "lead")
        ]);

        if (dbResponse.error || !dbResponse.data) {
            return rejectWithValue(dbResponse.error?.message ?? "Listing not found");
        }

        if (leadCountResponse.error) {
            return rejectWithValue(leadCountResponse.error.message);
        }

        return {
            ...mapDbListingToCarItem(dbResponse.data, currentUser),
            leadCount: leadCountResponse.count ?? 0
        };
    } catch (error) {
        if (error instanceof Error) {
            return rejectWithValue(error.message);
        }
        return rejectWithValue("An error occurred");
    }
});

const listingSlice = createSlice({
    name: "listing",
    initialState,
    reducers: {
        resetListings(state) {
            state.listings = [];
            state.currentListing = undefined;
            state.page = 0;
            state.totalPages = 0;
            state.fetchedPages = [];
            state.hasError = false;
            state.errorMessage = undefined;
        },
        setFilters(state, action: PayloadAction<ListingFilters>) {
            state.filters = {
                ...state.filters,
                ...action.payload
            };
            state.page = 0;
            state.totalPages = 0;
            state.fetchedPages = [];
            state.listings = [];
        },
        clearFilters(state) {
            state.filters = {};
            state.page = 0;
            state.totalPages = 0;
            state.fetchedPages = [];
            state.listings = [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchListingsAsync.pending, (state) => {
                state.loading = true;
                state.hasError = false;
                state.errorMessage = undefined;
            })
            .addCase(fetchListingsAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.hasError = false;

                const {page, totalPages, listings} = action.payload;

                state.page = page;
                state.totalPages = totalPages;

                if (!state.fetchedPages.includes(page)) {
                    state.fetchedPages.push(page);
                    state.listings.push(...listings);
                }
            })
            .addCase(fetchListingsAsync.rejected, (state, action) => {
                state.loading = false;
                state.hasError = true;
                state.errorMessage = (action.payload as string) ?? action.error.message ?? "Failed to fetch listings";
            })
            .addCase(setCurrentListingAsync.pending, (state) => {
                state.loading = true;
                state.hasError = false;
                state.errorMessage = undefined;
            })
            .addCase(setCurrentListingAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.currentListing = action.payload;
            })
            .addCase(setCurrentListingAsync.rejected, (state, action) => {
                state.loading = false;
                state.hasError = true;
                state.errorMessage = (action.payload as string) ?? action.error.message ?? "Failed to set current listing";
            })
            .addCase(createVehicleThunk.pending, (state) => {
                state.loading = true;
                state.hasError = false;
                state.errorMessage = undefined;
            })
            .addCase(createVehicleThunk.fulfilled, (state) => {
                state.loading = false;
                state.hasError = false;
            })
            .addCase(createVehicleThunk.rejected, (state, action) => {
                state.loading = false;
                state.hasError = true;
                state.errorMessage = (action.payload as string) ?? action.error.message ?? "Failed to create vehicle";
            });
    }
});

export const {resetListings, setFilters, clearFilters} = listingSlice.actions;

export default listingSlice;
